
import React, { useEffect, useRef } from "react";

interface HorizontalScrollProps {
    children: React.ReactNode;
    className?: string;
    onScroll?: (scrollLeft: number) => void;
    onReachEnd?: () => void;
    onReachStart?: () => void;
}

const HorizontalScroll = ({
    children,
    className = "",
    onScroll,
    onReachEnd,
    onReachStart,
}: HorizontalScrollProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const callbacksRef = useRef({ onScroll, onReachEnd, onReachStart });
    const dragRef = useRef({
        active: false,
        startX: 0,
        startLeft: 0,
        moved: false,
    });

    const atEndRef = useRef(false);
    const atStartRef = useRef(true);
    const suppressClickRef = useRef(false);

    useEffect(() => {
        callbacksRef.current = { onScroll, onReachEnd, onReachStart };
    }, [onScroll, onReachEnd, onReachStart]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const handleScroll = () => {
            const max = el.scrollWidth - el.clientWidth;
            const atEnd = max > 0 && el.scrollLeft >= max - 5;
            const atStart = el.scrollLeft <= 5;

            callbacksRef.current.onScroll?.(el.scrollLeft);

            if (atEnd && !atEndRef.current) {
                callbacksRef.current.onReachEnd?.();
            }

            if (atStart && !atStartRef.current) {
                callbacksRef.current.onReachStart?.();
            }

            atEndRef.current = atEnd;
            atStartRef.current = atStart;
        };

        const handleWheel = (e: WheelEvent) => {
            const max = el.scrollWidth - el.clientWidth;
            if (max <= 0) return;

            // Native horizontal gestures already work.
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

            const multiplier =
                e.deltaMode === 1 ? 16 :
                    e.deltaMode === 2 ? el.clientWidth : 1;

            const delta = e.deltaY * multiplier;
            const canScroll =
                (delta > 0 && el.scrollLeft < max - 1) ||
                (delta < 0 && el.scrollLeft > 1);

            if (canScroll) {
                e.preventDefault();
                el.scrollLeft += delta;
            }
        };

        const handleMove = (e: MouseEvent) => {
            const drag = dragRef.current;
            if (!drag.active) return;

            const distance = e.clientX - drag.startX;

            if (Math.abs(distance) > 5) {
                drag.moved = true;
                suppressClickRef.current = true;
            }

            if (drag.moved) {
                el.scrollLeft = drag.startLeft - distance;
                e.preventDefault();
            }
        };

        const handleUp = () => {
            dragRef.current.active = false;
        };

        el.addEventListener("scroll", handleScroll, { passive: true });
        el.addEventListener("wheel", handleWheel, { passive: false });
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
        window.addEventListener("blur", handleUp);

        return () => {
            el.removeEventListener("scroll", handleScroll);
            el.removeEventListener("wheel", handleWheel);
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
            window.removeEventListener("blur", handleUp);
        };
    }, []);

    return (
        <div
            ref={ref}
            className={className}
            onMouseDown={(e) => {
                if (e.button !== 0) return;
                if (
                    (e.target as HTMLElement).closest(
                        "button, a, input, select, textarea"
                    )
                ) return;

                dragRef.current = {
                    active: true,
                    startX: e.clientX,
                    startLeft: e.currentTarget.scrollLeft,
                    moved: false,
                };
                suppressClickRef.current = false;
            }}
            onClickCapture={(e) => {
                if (suppressClickRef.current) {
                    e.preventDefault();
                    e.stopPropagation();
                    suppressClickRef.current = false;
                }
            }}
            onDragStart={(e) => {
                if (e.target instanceof HTMLImageElement) {
                    e.preventDefault();
                }
            }}
        >
            {children}
        </div>
    );
};

export default HorizontalScroll;
